import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, getUser, clearAuth } from "../lib/apiClient";
import { toast } from "sonner";
import { GuildLookup } from "../components/GuildLookup";
import { SideNav } from "../components/SideNav";
import { ChildSafety } from "../components/ChildSafety";
import { Brand } from "../components/Brand";
import { Crown, LogOut, ArrowLeft, Megaphone, Ban, Users, Activity, ShieldOff, RefreshCcw, Send, Eye, Zap, Search, KeyRound, Server, Upload, PowerOff, Radio, UserCheck, UserX, Hash as HashIcon, Flame, MessageSquare, ShieldAlert } from "lucide-react";

const LOGO_URL = "https://customer-assets-4nw71qhi.emergentagent.net/job_server-control-panel-14/artifacts/mrn70swp_ChatGPT%20Image%20Aug%2015%2C%202026%2C%2010_53_11%20PM%20%281%29.png";

const OWNER_NAV = [
  { label: "Overview", items: [
    { id: "stats", label: "Global Stats", icon: Activity },
    { id: "feed", label: "Live Feed", icon: Zap },
    { id: "signals", label: "Signals", icon: Radio },
  ] },
  { label: "Lookup", items: [
    { id: "lookup", label: "User Lookup", icon: Search },
    { id: "guildlookup", label: "Guild Lookup", icon: Server },
    { id: "servers", label: "Servers", icon: Server },
  ] },
  { label: "Control", items: [
    { id: "announce", label: "Announcement", icon: Megaphone },
    { id: "ban", label: "Global Ban", icon: Ban },
    { id: "access", label: "Access Roles", icon: KeyRound },
  ] },
  { label: "ModNode+", items: [
    { id: "pro", label: "ModNode+ Grants", icon: Crown },
    { id: "support", label: "Support Queue", icon: Flame },
  ] },
  { label: "Trust & Safety", items: [
    { id: "childsafety", label: "Child Safety", icon: ShieldAlert, color: "text-[#EF4444]" },
  ] },
  { label: "Danger", items: [
    { id: "killswitch", label: "Kill Switch", icon: PowerOff, color: "text-[#EF4444]" },
  ] },
];

export default function Owner() {
  const navigate = useNavigate();
  const user = getUser();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(null); // null=checking, true/false
  const [tab, setTab] = useState("stats"); // stats | announce | ban
  const [tickets, setTickets] = useState([]);
  const [ticketCounts, setTicketCounts] = useState({ open: 0, in_progress: 0, awaiting_user: 0, closed: 0 });
  const [ticketFilter, setTicketFilter] = useState("all");
  const [replyDraft, setReplyDraft] = useState({});
  const [replyBusy, setReplyBusy] = useState({});
  const [ann, setAnn] = useState({ title: "", message: "" });
  const [banForm, setBanForm] = useState({ user_id: "", reason: "" });
  const [globalBans, setGlobalBans] = useState([]);
  const [posting, setPosting] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tplName, setTplName] = useState("");
  const [announceTargets, setAnnounceTargets] = useState([]);
  const [targetsLoading, setTargetsLoading] = useState(false);
  const [signals, setSignals] = useState([]);
  const [signalsCounts, setSignalsCounts] = useState({ verify: 0, bot_join: 0, bot_leave: 0 });
  const [signalFilter, setSignalFilter] = useState("all");
  const [signalsLoading, setSignalsLoading] = useState(false);
  const [proGrants, setProGrants] = useState([]);
  const [proGid, setProGid] = useState("");
  const [proNote, setProNote] = useState("");
  const [proNick, setProNick] = useState("");
  const [proExpires, setProExpires] = useState("");
  const [proLifetime, setProLifetime] = useState(false);  // explicit opt-in; default to timed grants
  const [proBusy, setProBusy] = useState(false);

  useEffect(() => {
    api.get("/owner/status").then(() => setAllowed(true)).catch(() => setAllowed(false));
  }, []);

  useEffect(() => {
    if (allowed !== true) return;
    reload();
  }, [allowed]);

  useEffect(() => {
    if (allowed === true && tab === "announce" && announceTargets.length === 0) {
      loadAnnounceTargets();
    }
    if (allowed === true && tab === "signals") {
      loadSignals(signalFilter);
    }
    if (allowed === true && tab === "pro") {
      loadProGrants();
    }
    if (allowed === true && tab === "support") {
      loadTickets(ticketFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, allowed, signalFilter, ticketFilter]);

  const reload = async () => {
    setLoading(true);
    try {
      const [s, b, t] = await Promise.all([
        api.get("/owner/global-stats"),
        api.get("/owner/global-bans"),
        api.get("/owner/announcement-templates"),
      ]);
      setStats(s.data);
      setGlobalBans(b.data.bans || []);
      setTemplates(t.data.templates || []);
    } catch (e) {
      toast.error("Failed to load owner data");
    } finally { setLoading(false); }
  };

  const applyTemplate = (t) => {
    setAnn({ title: t.title, message: t.message });
    toast.success(`Loaded "${t.name}"`);
  };

  const loadAnnounceTargets = async () => {
    setTargetsLoading(true);
    try {
      const r = await api.get("/owner/announcement-targets");
      setAnnounceTargets(r.data.targets || []);
    } catch (e) {
      toast.error("Failed to load announcement channels");
    } finally { setTargetsLoading(false); }
  };

  const setTargetChannel = async (guildId, channelId) => {
    try {
      await api.put(`/owner/announcement-targets/${guildId}`, { channel_id: channelId || null });
      setAnnounceTargets(list => list.map(t => t.guild_id === guildId ? { ...t, channel_id: channelId || null } : t));
      toast.success(channelId ? "Broadcast channel set" : "Reverted to auto-pick");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const loadSignals = async (type = signalFilter) => {
    setSignalsLoading(true);
    try {
      const r = await api.get(`/owner/signals${type && type !== "all" ? `?signal_type=${type}` : ""}`);
      setSignals(r.data.signals || []);
      setSignalsCounts(r.data.counts || { verify: 0, bot_join: 0, bot_leave: 0 });
    } catch (e) {
      toast.error("Failed to load signals");
    } finally { setSignalsLoading(false); }
  };

  const loadProGrants = async () => {
    try {
      const r = await api.get("/owner/pro-guilds");
      setProGrants(r.data.grants || []);
    } catch (e) {
      toast.error("Failed to load Pro grants");
    }
  };

  const grantPro = async () => {
    const gid = (proGid || "").trim();
    if (!gid || !/^\d+$/.test(gid)) {
      toast.error("Enter a valid Guild ID (snowflake)");
      return;
    }
    setProBusy(true);
    try {
      const payload = { guild_id: gid, note: proNote || null };
      if (proNick.trim()) payload.custom_nickname = proNick.trim().slice(0, 32);
      if (!proLifetime && proExpires) payload.expires_at = new Date(proExpires).toISOString();
      // When proLifetime=true, omit expires_at so the backend stores null → permanent
      const r = await api.post("/owner/pro-guilds", payload);
      setProGrants(g => [r.data.grant, ...g.filter(x => x.guild_id !== gid)]);
      setProGid(""); setProNote(""); setProNick(""); setProExpires(""); setProLifetime(false);
      toast.success(`ModNode+ ${proLifetime ? "LIFETIME " : ""}enabled on ${r.data.grant.guild_name || gid}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to grant Pro");
    } finally { setProBusy(false); }
  };

  const makeLifetime = async (gid, name) => {
    if (!window.confirm(`Convert ${name || gid} to a LIFETIME ModNode+ grant? The expiry date will be cleared.`)) return;
    try {
      const r = await api.patch(`/owner/pro-guilds/${gid}`, { lifetime: true });
      setProGrants(g => g.map(x => x.guild_id === gid ? r.data.grant : x));
      toast.success(`${name || gid} is now LIFETIME ModNode+`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to make lifetime");
    }
  };

  const updateProGrant = async (gid, patch) => {
    try {
      const r = await api.patch(`/owner/pro-guilds/${gid}`, patch);
      setProGrants(g => g.map(x => x.guild_id === gid ? r.data.grant : x));
      toast.success("Grant updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  const revokePro = async (gid, name) => {
    if (!window.confirm(`Revoke ModNode+ from ${name || gid}? The bot's nickname will revert to ModNode.`)) return;
    try {
      await api.delete(`/owner/pro-guilds/${gid}`);
      setProGrants(g => g.filter(x => x.guild_id !== gid));
      toast.success("ModNode+ revoked");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to revoke");
    }
  };

  const loadTickets = async (filter = "all") => {
    try {
      const r = await api.get("/owner/support-tickets" + (filter && filter !== "all" ? `?status=${filter}` : ""));
      setTickets(r.data.items || []);
      setTicketCounts(r.data.counts || {});
    } catch { toast.error("Failed to load support tickets"); }
  };
  const replyTicket = async (ticketId, status) => {
    const draft = (replyDraft[ticketId] || "").trim();
    if (!draft) { toast.error("Write a reply first"); return; }
    setReplyBusy(b => ({ ...b, [ticketId]: true }));
    try {
      const r = await api.post(`/owner/support-tickets/${ticketId}/reply`, { body: draft, status: status || undefined });
      setTickets(t => t.map(x => x.id === ticketId ? r.data.ticket : x));
      setReplyDraft(d => ({ ...d, [ticketId]: "" }));
      toast.success("Reply sent + DM'd");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reply failed");
    } finally { setReplyBusy(b => ({ ...b, [ticketId]: false })); }
  };
  const updateTicketStatus = async (ticketId, status) => {
    try {
      const r = await api.patch(`/owner/support-tickets/${ticketId}`, { status });
      setTickets(t => t.map(x => x.id === ticketId ? r.data.ticket : x));
    } catch (e) { toast.error(e?.response?.data?.detail || "Status change failed"); }
  };


  const saveTemplate = async () => {
    if (!tplName || !ann.title || !ann.message) return toast.error("Fill name, title and message first");
    try {
      await api.post("/owner/announcement-templates", { name: tplName, title: ann.title, message: ann.message });
      toast.success(`Saved template "${tplName}"`);
      setTplName("");
      reload();
    } catch { toast.error("Save failed"); }
  };

  const deleteTemplate = async (id, name) => {
    if (!confirm(`Delete template "${name}"?`)) return;
    try {
      await api.delete(`/owner/announcement-templates/${id}`);
      toast.success("Template deleted");
      reload();
    } catch { toast.error("Delete failed"); }
  };

  const sendAnnounce = async () => {
    if (!ann.title || !ann.message) return toast.error("Title and message required");
    setPosting(true);
    try {
      const r = await api.post("/owner/announce", ann);
      toast.success(`Sent to ${r.data.sent}/${r.data.total} servers`);
      setAnn({ title: "", message: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Announce failed");
    } finally { setPosting(false); }
  };

  const runGlobalBan = async () => {
    if (!banForm.user_id) return toast.error("user_id required");
    if (!confirm(`Global ban ${banForm.user_id} from every ModNode server?`)) return;
    try {
      const r = await api.post("/owner/global-ban", banForm);
      toast.success(`Banned in ${r.data.guilds_banned}/${r.data.guilds_total} servers`);
      setBanForm({ user_id: "", reason: "" });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Global ban failed");
    }
  };

  const removeGlobalBan = async (uid) => {
    if (!confirm(`Lift global ban on ${uid}?`)) return;
    try {
      const r = await api.delete(`/owner/global-ban/${uid}`);
      toast.success(`Unbanned in ${r.data.unbanned}/${r.data.total} servers`);
      reload();
    } catch (e) { toast.error("Failed"); }
  };

  const logout = () => { clearAuth(); navigate("/"); };

  if (allowed === null) {
    return (
      <div className="min-h-screen bg-[#06070A] flex items-center justify-center">
        <div className="font-mono text-[#10B981] tracking-widest text-sm">
          <span className="pulse-dot inline-block w-2 h-2 bg-[#10B981] mr-3" />
          CHECKING CLEARANCE...
        </div>
      </div>
    );
  }
  if (allowed === false) {
    return (
      <div className="min-h-screen bg-[#06070A] flex flex-col items-center justify-center gap-4 p-8" data-testid="owner-forbidden">
        <ShieldOff className="w-16 h-16 text-[#EF4444]" />
        <div className="font-display font-black text-3xl uppercase">FOUNDER ONLY</div>
        <p className="text-[#94A3B8] max-w-md text-center">
          This panel is restricted to ModNode founders. Add your Discord user ID to <code className="text-[#10B981]">OWNER_USER_IDS</code> in <code className="text-[#10B981]">backend/.env</code> to unlock.
        </p>
        <button onClick={() => navigate("/servers")} data-testid="back-to-servers" className="mt-4 px-6 py-3 border border-white/10 hover:border-[#8B5CF6] hover:text-[#8B5CF6] font-mono text-xs tracking-widest">
          ← BACK
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#06070A] mm-grid-bg text-white" data-testid="owner-page">
      <div className="absolute inset-0 mm-noise pointer-events-none" />

      <div className="relative z-10 flex flex-col lg:flex-row">
      <SideNav groups={OWNER_NAV} active={tab} onSelect={t => setTab(t.id)} testidPrefix="owner-tab" header={<Brand size="sm" />}
        footer={<div className="px-3 py-3 rounded-xl border border-[#F59E0B]/20 bg-[#F59E0B]/5 text-xs text-[#F59E0B] flex items-center gap-2"><Crown className="w-3.5 h-3.5" /> Founder access</div>} />
      <div className="flex-1 min-w-0">
      {/* Top bar */}
      <div className="relative lg:sticky lg:top-0 z-20 border-b border-white/5 bg-[#06070A]/70 backdrop-blur-xl flex items-center justify-between px-6 py-3">
        <div className="flex items-center gap-4">
          <button data-testid="owner-back-btn" onClick={() => navigate("/servers")} className="p-2 border border-white/10 hover:border-[#8B5CF6] hover:text-white text-[#94A3B8]">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="font-display font-semibold">Founder Console</div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={reload} data-testid="owner-refresh-btn" className="p-2 border border-white/10 hover:border-[#8B5CF6] text-[#94A3B8] hover:text-white">
            <RefreshCcw className="w-4 h-4" />
          </button>
          <span className="hidden md:block text-sm text-[#94A3B8]">{user?.global_name || user?.username}</span>
          <button data-testid="owner-logout-btn" onClick={logout} className="p-2 border border-white/10 hover:border-[#EF4444] hover:text-[#EF4444] text-[#94A3B8]">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight" data-testid="owner-section-title">
          {OWNER_NAV.flatMap(g => g.items).find(i => i.id === tab)?.label}
        </h1>
        {loading && tab === "stats" && <div className="font-mono text-sm text-[#10B981]">// loading...</div>}

        {/* STATS */}
        {tab === "stats" && stats && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="owner-stats">
              <OwnerStat label="Total Servers" value={stats.total_guilds} accent="cyan" />
              <OwnerStat label="Total Members" value={stats.total_members?.toLocaleString()} accent="volt" />
              <OwnerStat label="Total Online" value={stats.total_online?.toLocaleString()} accent="volt" />
              <OwnerStat label="Total Channels" value={stats.total_channels?.toLocaleString()} accent="warn" />
              <OwnerStat label="Total Roles" value={stats.total_roles?.toLocaleString()} accent="cyan" />
              <OwnerStat label="Warnings" value={stats.total_warnings} accent="warn" />
              <OwnerStat label="Mod Actions" value={stats.total_actions} accent="danger" />
              <OwnerStat label="Global Bans" value={stats.total_global_bans} accent="danger" />
            </div>

            <div className="border border-white/10 bg-[#0D0E15]">
              <div className="px-5 py-3 border-b border-white/10 font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">All Servers ({stats.guilds?.length || 0})</div>
              <div className="max-h-[60vh] overflow-y-auto">
                {stats.guilds?.map(g => (
                  <div key={g.id} className="flex items-center gap-3 px-5 py-3 border-b border-white/5 hover:bg-[#141622]">
                    {g.icon_url ? <img src={g.icon_url} alt="" className="w-10 h-10 rounded-md" /> : (
                      <div className="w-10 h-10 rounded-md bg-[#141622] flex items-center justify-center font-display font-bold text-[#8B5CF6]">
                        {g.name?.slice(0,2).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{g.name}</div>
                      <div className="font-mono text-[10px] text-[#64748B]">{g.id}</div>
                    </div>
                    <div className="hidden md:flex items-center gap-4 font-mono text-[10px] text-[#94A3B8]">
                      <span><Users className="w-3 h-3 inline mr-1 text-[#8B5CF6]" />{g.members?.toLocaleString()}</span>
                      <span className="text-[#10B981]">● {g.online?.toLocaleString()}</span>
                      <span className="text-[#F59E0B]">#{g.channels}</span>
                    </div>
                    <button
                      onClick={() => navigate(`/dashboard/${g.id}`)}
                      className="px-3 py-1.5 border border-white/10 hover:border-[#8B5CF6] hover:text-[#8B5CF6] font-mono text-[10px] tracking-widest"
                    >OPEN</button>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ANNOUNCE */}
        {tab === "announce" && (
          <div className="space-y-4" data-testid="owner-announce">
            {/* Templates */}
            <div className="border border-white/10 bg-[#0D0E15] p-5">
              <div className="flex items-center gap-2 mb-3">
                <Megaphone className="w-4 h-4 text-[#10B981]" />
                <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Templates · {templates.length}</span>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="template-chips">
                {templates.map(t => (
                  <div key={t.id} className="group relative inline-flex items-stretch border border-[#10B981]/30 hover:border-[#10B981] transition-colors">
                    <button
                      data-testid={`tpl-apply-${t.id}`}
                      onClick={() => applyTemplate(t)}
                      className="px-3 py-2 font-mono text-[10px] tracking-widest text-[#10B981] hover:bg-[#10B981]/5"
                    >{t.name.toUpperCase()}</button>
                    <button
                      data-testid={`tpl-del-${t.id}`}
                      onClick={() => deleteTemplate(t.id, t.name)}
                      className="px-2 border-l border-[#10B981]/30 text-[#64748B] hover:text-[#EF4444] hover:bg-[#EF4444]/10"
                      title="Delete template"
                    >×</button>
                  </div>
                ))}
                {templates.length === 0 && (
                  <div className="font-mono text-xs text-[#64748B]">// no templates saved yet</div>
                )}
              </div>
            </div>

            <div className="border border-white/10 bg-[#0D0E15] p-6">
              <h2 className="font-display font-black text-xl mb-2">GLOBAL ANNOUNCEMENT</h2>
              <p className="text-sm text-[#94A3B8] mb-2">
                Send a rich <span className="text-[#10B981]">Components V2</span> message to every ModNode server. Posts to the system channel (or first text channel).
              </p>
              <div className="inline-flex items-center gap-2 mb-6 px-2 py-1 border border-[#10B981]/40 bg-[#10B981]/5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] shadow-[0_0_6px_#10B981]" />
                <span className="font-mono text-[10px] tracking-widest text-[#10B981]">DISCORD COMPONENTS V2 · FLAG 32768</span>
              </div>
              <label className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2 block">Title</label>
              <input
                data-testid="announce-title"
                value={ann.title}
                onChange={e => setAnn(a => ({ ...a, title: e.target.value }))}
                placeholder="e.g. Scheduled Maintenance"
                className="w-full bg-[#06070A] border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-[#10B981] mb-4"
              />
              <label className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2 block">Message</label>
              <textarea
                data-testid="announce-message"
                value={ann.message}
                onChange={e => setAnn(a => ({ ...a, message: e.target.value }))}
                placeholder="Describe what's happening..."
                rows={6}
                className="w-full bg-[#06070A] border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-[#10B981] mb-4 resize-y"
              />

              {/* V2 Live Preview */}
              <div className="mb-4" data-testid="v2-preview-wrap">
                <div className="flex items-center gap-2 mb-2">
                  <Eye className="w-3.5 h-3.5 text-[#8B5CF6]" />
                  <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Live V2 Preview</span>
                </div>
                <V2Preview title={ann.title} message={ann.message} accent="#10B981" footer="ModNode · Announcement" />
              </div>
              <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                <button
                  data-testid="announce-send-btn"
                  onClick={sendAnnounce}
                  disabled={posting}
                  className="px-6 py-3 bg-[#10B981] text-black font-display font-bold hover:bg-[#10B981]/80 disabled:opacity-50 flex items-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  {posting ? "BROADCASTING..." : `BROADCAST TO ${stats?.total_guilds || 0} SERVERS`}
                </button>
                <div className="flex-1 flex items-center gap-2">
                  <input
                    data-testid="tpl-name-input"
                    value={tplName}
                    onChange={e => setTplName(e.target.value)}
                    placeholder="Save current as template..."
                    className="flex-1 bg-[#06070A] border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-[#10B981]"
                  />
                  <button
                    data-testid="tpl-save-btn"
                    onClick={saveTemplate}
                    className="px-4 py-2.5 border border-[#8B5CF6]/40 hover:border-[#8B5CF6] hover:text-[#8B5CF6] font-mono text-[10px] tracking-widest"
                  >SAVE TEMPLATE</button>
                </div>
              </div>
            </div>

            {/* Broadcast Channels — founder picks where announcements land per server */}
            <div className="border border-white/10 bg-[#0D0E15] p-5" data-testid="announce-targets-card">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Server className="w-4 h-4 text-[#8B5CF6]" />
                  <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">
                    Broadcast Channels · {announceTargets.length}
                  </span>
                </div>
                <button
                  data-testid="announce-targets-reload"
                  onClick={loadAnnounceTargets}
                  className="px-3 py-1.5 border border-white/10 hover:border-white/25 font-mono text-[10px] tracking-widest text-[#94A3B8] flex items-center gap-1.5"
                >
                  <RefreshCcw className="w-3 h-3" /> RELOAD
                </button>
              </div>
              <p className="text-xs text-[#64748B] mb-4 font-mono">
                // pick where global announcements land in each server. Leave blank to auto-pick (system channel → first text channel).
              </p>
              {targetsLoading && <div className="font-mono text-xs text-[#8B5CF6]">// loading channels…</div>}
              {!targetsLoading && announceTargets.length === 0 && (
                <div className="font-mono text-xs text-[#64748B]">// no servers found</div>
              )}
              <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
                {announceTargets.map(t => (
                  <div
                    key={t.guild_id}
                    className="flex items-center justify-between gap-3 px-3 py-2 border border-white/5 bg-[#06070A] hover:border-white/10 transition-colors"
                    data-testid={`announce-target-${t.guild_id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {t.guild_icon ? (
                        <img
                          alt=""
                          src={`https://cdn.discordapp.com/icons/${t.guild_id}/${t.guild_icon}.png?size=64`}
                          className="w-7 h-7 rounded-md border border-white/10 flex-shrink-0"
                        />
                      ) : (
                        <div className="w-7 h-7 rounded-md border border-white/10 bg-[#0D0E15] flex items-center justify-center text-[10px] text-[#64748B] flex-shrink-0">
                          {(t.guild_name || "?").slice(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="text-sm text-white font-display font-bold truncate">{t.guild_name || t.guild_id}</div>
                        <div className="font-mono text-[10px] text-[#64748B]">
                          {t.channel_id
                            ? <>◉ posts to #{(t.channels.find(c => c.id === t.channel_id) || {}).name || t.channel_id}</>
                            : <>○ auto-pick (system channel)</>
                          }
                        </div>
                      </div>
                    </div>
                    <select
                      data-testid={`announce-target-select-${t.guild_id}`}
                      value={t.channel_id || ""}
                      onChange={e => setTargetChannel(t.guild_id, e.target.value)}
                      className="bg-[#0D0E15] border border-white/10 px-2 py-1.5 text-xs outline-none focus:border-[#8B5CF6] font-mono min-w-[180px]"
                    >
                      <option value="">— auto-pick —</option>
                      {t.channels.map(c => (
                        <option key={c.id} value={c.id}>#{c.name}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* GLOBAL BAN */}
        {tab === "ban" && (
          <div className="space-y-6" data-testid="owner-ban">
            <div className="border border-[#EF4444]/40 bg-[#EF4444]/5 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Ban className="w-5 h-5 text-[#EF4444]" />
                <h2 className="font-display font-black text-xl">GLOBAL BAN</h2>
              </div>
              <p className="text-sm text-[#94A3B8] mb-6">
                Ban a Discord user ID from <span className="text-[#EF4444]">every server</span> ModNode is in. Use carefully — this is nuclear.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <input
                  data-testid="gban-userid"
                  value={banForm.user_id}
                  onChange={e => setBanForm(b => ({ ...b, user_id: e.target.value }))}
                  placeholder="Discord user ID"
                  className="bg-[#06070A] border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-[#EF4444]"
                />
                <input
                  data-testid="gban-reason"
                  value={banForm.reason}
                  onChange={e => setBanForm(b => ({ ...b, reason: e.target.value }))}
                  placeholder="Reason"
                  className="md:col-span-1 bg-[#06070A] border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-[#EF4444]"
                />
                <button
                  data-testid="gban-submit"
                  onClick={runGlobalBan}
                  className="bg-[#EF4444] hover:bg-[#DC2626] text-white font-display font-bold px-6 py-2.5 flex items-center justify-center gap-2"
                >
                  <Ban className="w-4 h-4" /> GLOBAL BAN
                </button>
              </div>
            </div>

            {/* Sync panel */}
            <GlobalBanSyncPanel />
            <BulkBanImport onImport={reload} />

            <div className="border border-white/10 bg-[#0D0E15]">
              <div className="px-5 py-3 border-b border-white/10 font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Global Ban Registry · {globalBans.length}</div>
              <div className="max-h-[50vh] overflow-y-auto" data-testid="gban-list">
                {globalBans.length === 0 && <div className="p-6 text-center font-mono text-xs text-[#64748B]">// no global bans issued</div>}
                {globalBans.map(b => (
                  <div key={b.id} className="flex items-center gap-3 px-5 py-3 border-b border-white/5">
                    <Ban className="w-4 h-4 text-[#EF4444]" />
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-sm">{b.user_id}</div>
                      <div className="text-xs text-[#94A3B8] truncate">{b.reason}</div>
                      <div className="font-mono text-[10px] text-[#64748B]">
                        {b.guilds_banned}/{b.guilds_total} servers · {new Date(b.created_at).toLocaleString()}
                      </div>
                    </div>
                    <button
                      onClick={() => removeGlobalBan(b.user_id)}
                      data-testid={`gban-lift-${b.user_id}`}
                      className="px-3 py-1.5 border border-[#10B981]/40 hover:border-[#10B981] hover:text-[#10B981] font-mono text-[10px] tracking-widest"
                    >LIFT</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* LIVE FEED */}
        {tab === "feed" && <FoundersFeed />}

        {/* USER LOOKUP */}
        {tab === "lookup" && <UserLookup />}
        {tab === "guildlookup" && <GuildLookup />}
        {tab === "childsafety" && <ChildSafety />}

        {/* ACCESS ROLES */}
        {tab === "access" && <AccessRoleManager />}

        {/* SERVERS */}
        {tab === "servers" && <ServerManager />}

        {/* SIGNALS — verify + bot-join timeline */}
        {tab === "signals" && (
          <div className="space-y-4" data-testid="owner-signals">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <SignalPill label="Verifications"  value={signalsCounts.verify}    icon={UserCheck} color="green" />
              <SignalPill label="Bot Added"      value={signalsCounts.bot_join}  icon={Server}    color="blurple" />
              <SignalPill label="Bot Removed"    value={signalsCounts.bot_leave} icon={UserX}     color="red" />
            </div>

            <div className="border border-white/10 bg-[#0D0E15] p-5">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <Radio className="w-4 h-4 text-[#8B5CF6]" />
                  <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">
                    Timeline · {signals.length} shown
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {[
                    { id: "all",       label: "ALL" },
                    { id: "verify",    label: "VERIFIES" },
                    { id: "bot_join",  label: "JOINS" },
                    { id: "bot_leave", label: "LEAVES" },
                  ].map(f => (
                    <button
                      key={f.id}
                      data-testid={`signals-filter-${f.id}`}
                      onClick={() => setSignalFilter(f.id)}
                      className={`px-2.5 py-1 border font-mono text-[10px] tracking-widest transition-colors ${
                        signalFilter === f.id
                          ? "border-[#8B5CF6]/60 text-[#8B5CF6] bg-[#8B5CF6]/10"
                          : "border-white/10 text-[#64748B] hover:border-white/25 hover:text-white"
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                  <button
                    data-testid="signals-reload"
                    onClick={() => loadSignals(signalFilter)}
                    className="ml-2 p-1.5 border border-white/10 hover:border-white/25 text-[#94A3B8]"
                  >
                    <RefreshCcw className="w-3 h-3" />
                  </button>
                </div>
              </div>

              {signalsLoading && <div className="font-mono text-xs text-[#8B5CF6]">// loading signals…</div>}
              {!signalsLoading && signals.length === 0 && (
                <div className="font-mono text-xs text-[#64748B] py-8 text-center">// no signals yet · verifications & bot joins show up here as they happen</div>
              )}
              <div className="space-y-1 max-h-[560px] overflow-y-auto pr-1">
                {signals.map(s => (
                  <SignalRow key={s.id} signal={s} />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* MODNODE+ */}
        {tab === "pro" && (
          <div className="space-y-5" data-testid="owner-pro">
            <div className="border border-[#F59E0B]/30 bg-gradient-to-br from-[#F59E0B]/5 via-[#0D0E15] to-[#0D0E15] p-6">
              <div className="flex items-center gap-3 mb-3">
                <Crown className="w-6 h-6 text-[#F59E0B]" />
                <div>
                  <div className="font-display font-black text-2xl text-white">ModNode<span className="text-[#F59E0B]">+</span></div>
                  <div className="font-mono text-[10px] tracking-widest text-[#F59E0B] uppercase">Founder-granted premium tier</div>
                </div>
              </div>
              <p className="text-sm text-[#94A3B8] max-w-2xl">
                Grant any guild the Pro tier. The bot's nickname in that server automatically becomes <span className="text-[#F59E0B] font-mono">ModNode+</span> and unlocks the full AI model catalog, higher caps, and premium-only perks.
              </p>
            </div>

            {/* Grant form */}
            <div className="border border-white/10 bg-[#0D0E15] p-5">
              <div className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-3">Grant ModNode+</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="font-mono text-[9px] tracking-widest text-[#64748B] uppercase mb-1 block">Guild ID *</label>
                  <input
                    data-testid="pro-guild-id-input"
                    value={proGid}
                    onChange={e => setProGid(e.target.value)}
                    placeholder="Guild ID (snowflake)"
                    className="w-full bg-[#06070A] border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#F59E0B] font-mono"
                  />
                </div>
                <div>
                  <label className="font-mono text-[9px] tracking-widest text-[#64748B] uppercase mb-1 block">Custom Nickname</label>
                  <input
                    data-testid="pro-nick-input"
                    value={proNick}
                    onChange={e => setProNick(e.target.value)}
                    placeholder="ModNode+ (default)"
                    maxLength={32}
                    className="w-full bg-[#06070A] border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#F59E0B] font-mono"
                  />
                </div>
                <div>
                  <label className="font-mono text-[9px] tracking-widest text-[#64748B] uppercase mb-1 block">Duration</label>
                  <div className="flex items-stretch gap-2">
                    <button
                      type="button"
                      data-testid="pro-lifetime-toggle"
                      onClick={() => setProLifetime(l => !l)}
                      className={`px-3 py-2 border font-mono text-xs tracking-widest transition-all whitespace-nowrap ${proLifetime ? "bg-[#F59E0B] text-black border-[#F59E0B] font-bold" : "border-white/10 text-[#94A3B8] hover:border-white/30"}`}
                    >
                      ∞ LIFETIME
                    </button>
                    <input
                      type="datetime-local"
                      data-testid="pro-expires-input"
                      disabled={proLifetime}
                      value={proExpires}
                      onChange={e => setProExpires(e.target.value)}
                      placeholder="Expires at"
                      className="flex-1 bg-[#06070A] border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#F59E0B] font-mono disabled:opacity-40"
                    />
                  </div>
                  <div className="font-mono text-[9px] text-[#64748B] mt-1">
                    {proLifetime ? "Grant never expires." : "Grant will auto-expire on this date."}
                  </div>
                </div>
                <div>
                  <label className="font-mono text-[9px] tracking-widest text-[#64748B] uppercase mb-1 block">Note</label>
                  <input
                    data-testid="pro-note-input"
                    value={proNote}
                    onChange={e => setProNote(e.target.value)}
                    placeholder="why this guild gets Pro"
                    className="w-full bg-[#06070A] border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#F59E0B] font-mono"
                  />
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  data-testid="pro-grant-btn"
                  onClick={grantPro}
                  disabled={proBusy || !proGid.trim()}
                  className="px-5 py-2 bg-[#F59E0B] hover:bg-[#D99A1A] text-black font-display font-bold text-sm disabled:opacity-50 flex items-center gap-2"
                >
                  <Crown className="w-4 h-4" /> Grant Pro
                </button>
              </div>
              <div className="mt-2 font-mono text-[10px] text-[#64748B]">
                // guild must have the bot already installed · nickname updates immediately · toggle Lifetime for permanent grants
              </div>
            </div>

            {/* Perks table */}
            <div className="border border-white/10 bg-[#0D0E15] p-5">
              <div className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-3">What Pro unlocks</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 font-mono text-xs">
                {[
                  ["Custom flagged words",     "25",           "500"],
                  ["AI models",                "Claude 4.6 · Haiku · GPT-5.4-mini · Gemini Flash", "Full catalog (Opus 4.8 · GPT-5.6 Sol · Gemini 3.1 Pro …)"],
                  ["Max auto-action severity", "high",         "critical (auto-ban)"],
                  ["Trusted roles",            "5",            "100"],
                  ["Excluded channels",        "5",            "100"],
                  ["Music queue length",       "25",           "500"],
                  ["Audit retention",          "30 days",      "365 days"],
                  ["Signal detail",            "basic",        "full fingerprint"],
                  ["Priority support",         "—",            "yes"],
                  ["Bot nickname",             "ModNode",      "ModNode+"],
                ].map(([k, free, pro], i) => (
                  <div key={i} className="flex items-start gap-3 py-1.5 border-b border-white/5">
                    <span className="text-[#64748B] text-[10px] tracking-widest uppercase min-w-[160px] pt-0.5">{k}</span>
                    <span className="text-[#94A3B8] flex-1">{free}</span>
                    <span className="text-[#F59E0B] flex-1">{pro}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Current grants */}
            <div className="border border-white/10 bg-[#0D0E15] p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Active grants · {proGrants.length}</span>
                <button
                  data-testid="pro-reload"
                  onClick={loadProGrants}
                  className="p-1.5 border border-white/10 hover:border-white/25 text-[#94A3B8]"
                >
                  <RefreshCcw className="w-3 h-3" />
                </button>
              </div>
              {proGrants.length === 0 && (
                <div className="font-mono text-xs text-[#64748B] py-6 text-center">// no Pro guilds yet</div>
              )}
              <div className="space-y-1.5">
                {proGrants.map(g => {
                  const expiryMs = g.expires_at ? new Date(g.expires_at).getTime() : null;
                  const daysLeft = expiryMs ? Math.ceil((expiryMs - Date.now()) / 86400000) : null;
                  const expired = daysLeft !== null && daysLeft < 0;
                  const lifetime = !g.expires_at;
                  return (
                    <div key={g.guild_id} className={`flex items-center gap-3 px-3 py-2 border ${expired ? "border-[#EF4444]/40 bg-[#EF4444]/5" : lifetime ? "border-[#F59E0B]/50 bg-[#F59E0B]/10" : "border-[#F59E0B]/30 bg-[#F59E0B]/5"}`} data-testid={`pro-grant-${g.guild_id}`}>
                      {g.guild_icon ? (
                        <img alt="" src={`https://cdn.discordapp.com/icons/${g.guild_id}/${g.guild_icon}.png?size=64`} className="w-8 h-8 rounded-md border border-[#F59E0B]/40" />
                      ) : (
                        <div className="w-8 h-8 rounded-md border border-[#F59E0B]/40 bg-[#0D0E15] flex items-center justify-center text-[10px] text-[#F59E0B]">
                          {(g.guild_name || "?").slice(0,2).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-bold text-sm text-white truncate flex items-center gap-2">
                          {g.guild_name || g.guild_id}
                          <span className="px-1.5 py-0.5 border border-[#F59E0B]/50 text-[#F59E0B] text-[9px] font-mono tracking-widest">{g.custom_nickname || "ModNode+"}</span>
                          {lifetime && (
                            <span data-testid={`pro-lifetime-badge-${g.guild_id}`} className="px-1.5 py-0.5 border border-[#F59E0B] bg-[#F59E0B]/20 text-[#F59E0B] text-[9px] font-mono tracking-widest font-bold">∞ LIFETIME</span>
                          )}
                        </div>
                        <div className="font-mono text-[10px] text-[#64748B] truncate">
                          {g.guild_id} · granted {new Date(g.granted_at).toLocaleDateString()}
                          {lifetime
                            ? <span className="text-[#F59E0B]"> · never expires</span>
                            : daysLeft !== null && <span className={expired ? "text-[#EF4444]" : daysLeft <= 7 ? "text-[#F59E0B]" : "text-[#10B981]"}>{` · ${expired ? `expired ${-daysLeft}d ago` : `${daysLeft}d left`}`}</span>
                          }
                          {g.note ? ` · "${g.note}"` : ""}
                        </div>
                      </div>
                      {!lifetime && (
                        <button
                          data-testid={`pro-make-lifetime-${g.guild_id}`}
                          onClick={() => makeLifetime(g.guild_id, g.guild_name)}
                          className="px-3 py-1 border border-[#F59E0B]/50 text-[#F59E0B] hover:bg-[#F59E0B]/10 font-display font-bold text-xs"
                          title="Convert to permanent grant"
                        >
                          ∞ Lifetime
                        </button>
                      )}
                      <button
                        data-testid={`pro-rename-${g.guild_id}`}
                        onClick={() => {
                          const nick = window.prompt(`New bot nickname for ${g.guild_name || g.guild_id}? (blank = ModNode+)`, g.custom_nickname || "");
                          if (nick === null) return;
                          updateProGrant(g.guild_id, { custom_nickname: nick.trim().slice(0, 32) });
                        }}
                        className="px-3 py-1 border border-[#8B5CF6]/40 text-[#8B5CF6] hover:bg-[#8B5CF6]/10 font-display font-bold text-xs"
                      >
                        Rename
                      </button>
                      <button
                        data-testid={`pro-revoke-${g.guild_id}`}
                        onClick={() => revokePro(g.guild_id, g.guild_name)}
                        className="px-3 py-1 border border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10 font-display font-bold text-xs"
                      >
                        Revoke
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* SUPPORT QUEUE */}
        {tab === "support" && (
          <div className="space-y-4" data-testid="owner-support">
            <div className="border border-white/10 bg-gradient-to-br from-[#0D0E15] to-[#06070A] p-6">
              <div className="flex items-center gap-3 mb-2">
                <Flame className="w-5 h-5 text-[#F59E0B]" />
                <div className="font-display font-black text-xl tracking-widest text-white">PREMIUM SUPPORT QUEUE</div>
              </div>
              <p className="text-sm text-[#94A3B8] max-w-2xl">
                Tickets opened by ModNode+ guild admins. Replies DM the author directly with the moderator-only context attached.
              </p>
            </div>

            {/* Filter + counters */}
            <div className="border border-white/10 bg-[#0D0E15] p-4 flex items-center gap-2 flex-wrap">
              {[
                { k: "all",            label: "ALL" },
                { k: "open",           label: `OPEN · ${ticketCounts.open || 0}` },
                { k: "in_progress",    label: `IN PROGRESS · ${ticketCounts.in_progress || 0}` },
                { k: "awaiting_user",  label: `AWAITING USER · ${ticketCounts.awaiting_user || 0}` },
                { k: "closed",         label: `CLOSED · ${ticketCounts.closed || 0}` },
              ].map(f => (
                <button
                  key={f.k}
                  data-testid={`ticket-filter-${f.k}`}
                  onClick={() => setTicketFilter(f.k)}
                  className={`px-3 py-1.5 border font-mono text-[10px] tracking-widest transition-all ${ticketFilter === f.k ? "bg-[#F59E0B]/10 border-[#F59E0B] text-[#F59E0B]" : "border-white/10 text-[#64748B] hover:border-white/30"}`}
                >
                  {f.label}
                </button>
              ))}
              <button data-testid="ticket-reload" onClick={() => loadTickets(ticketFilter)} className="ml-auto p-1.5 border border-white/10 hover:border-white/25 text-[#94A3B8]">
                <RefreshCcw className="w-3 h-3" />
              </button>
            </div>

            {/* Ticket list */}
            <div className="space-y-3">
              {tickets.length === 0 && (
                <div className="border border-white/10 bg-[#0D0E15] p-8 font-mono text-xs text-[#64748B] text-center">
                  // no tickets · premium admins can open tickets from their Dashboard → MODNODE+ tab
                </div>
              )}
              {tickets.map(t => (
                <div key={t.id} className="border border-white/10 bg-[#0D0E15] p-4" data-testid={`ticket-${t.id}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className={`px-2 py-0.5 font-mono text-[9px] tracking-widest ${t.priority === "urgent" ? "bg-[#EF4444]/20 text-[#EF4444] border border-[#EF4444]/40" : t.priority === "high" ? "bg-[#F59E0B]/20 text-[#F59E0B] border border-[#F59E0B]/40" : t.priority === "normal" ? "bg-[#8B5CF6]/20 text-[#8B5CF6] border border-[#8B5CF6]/40" : "bg-white/10 text-[#64748B] border border-white/10"}`}>{(t.priority || "normal").toUpperCase()}</span>
                    <span className="font-display font-bold text-base text-white">{t.subject}</span>
                    <span className="ml-auto flex items-center gap-1 text-[10px] font-mono text-[#64748B]">
                      <Server className="w-3 h-3" /> {t.guild_name || t.guild_id}
                    </span>
                  </div>
                  <div className="font-mono text-[10px] text-[#64748B] mb-2">
                    By &lt;@{t.user_id}&gt; · opened {new Date(t.created_at).toLocaleString()} · status: <span className={t.status === "closed" ? "text-[#64748B]" : "text-[#10B981]"}>{t.status}</span>
                  </div>
                  <div className="font-mono text-xs text-[#94A3B8] whitespace-pre-wrap mb-3 border-l-2 border-white/10 pl-3">{t.body}</div>

                  {(t.replies || []).length > 0 && (
                    <div className="space-y-2 mb-3" data-testid={`ticket-replies-${t.id}`}>
                      {t.replies.map(r => (
                        <div key={r.id} className="border border-white/5 bg-[#06070A] p-2">
                          <div className="font-mono text-[10px] text-[#F59E0B] mb-1">
                            <span className="text-[#F59E0B]">{r.role || "founder"}</span> &lt;@{r.by}&gt; · {new Date(r.at).toLocaleString()}
                          </div>
                          <div className="font-mono text-xs text-[#94A3B8] whitespace-pre-wrap">{r.body}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  <textarea
                    data-testid={`ticket-reply-input-${t.id}`}
                    value={replyDraft[t.id] || ""}
                    onChange={e => setReplyDraft(d => ({ ...d, [t.id]: e.target.value }))}
                    placeholder="Reply as founder — the ticket author gets a DM instantly."
                    rows={3}
                    className="w-full bg-[#06070A] border border-white/10 px-3 py-2 text-xs font-mono outline-none focus:border-[#F59E0B] mb-2"
                  />
                  <div className="flex items-center gap-2 flex-wrap">
                    <button data-testid={`ticket-reply-${t.id}`} disabled={replyBusy[t.id]} onClick={() => replyTicket(t.id, "in_progress")} className="px-3 py-1.5 bg-[#F59E0B] hover:bg-[#D99A1A] text-black font-display font-bold text-xs disabled:opacity-50 flex items-center gap-1.5">
                      <Send className="w-3 h-3" /> {replyBusy[t.id] ? "SENDING…" : "REPLY + MARK IN-PROGRESS"}
                    </button>
                    <button data-testid={`ticket-reply-close-${t.id}`} disabled={replyBusy[t.id]} onClick={() => replyTicket(t.id, "closed")} className="px-3 py-1.5 border border-[#10B981]/50 text-[#10B981] hover:bg-[#10B981]/10 font-display font-bold text-xs disabled:opacity-50 flex items-center gap-1.5">
                      <MessageSquare className="w-3 h-3" /> REPLY + CLOSE
                    </button>
                    {t.status !== "closed" ? (
                      <button data-testid={`ticket-close-${t.id}`} onClick={() => updateTicketStatus(t.id, "closed")} className="ml-auto px-3 py-1 border border-white/10 text-[#64748B] hover:border-white/30 font-display font-bold text-xs">
                        CLOSE (no reply)
                      </button>
                    ) : (
                      <button data-testid={`ticket-reopen-${t.id}`} onClick={() => updateTicketStatus(t.id, "open")} className="ml-auto px-3 py-1 border border-[#8B5CF6]/40 text-[#8B5CF6] hover:bg-[#8B5CF6]/10 font-display font-bold text-xs">
                        RE-OPEN
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* KILL SWITCH */}
        {tab === "killswitch" && <KillSwitch />}
      </div>
      </div>
      </div>
    </div>
  );
}

function SignalPill({ label, value, icon: Icon, color }) {
  const palette = {
    green:   { b: "border-[#10B981]/40", t: "text-[#10B981]" },
    blurple: { b: "border-[#8B5CF6]/40", t: "text-[#8B5CF6]" },
    red:     { b: "border-[#EF4444]/40", t: "text-[#EF4444]" },
  }[color] || { b: "border-white/10", t: "text-white" };
  return (
    <div className={`border ${palette.b} bg-[#0D0E15] p-4 flex items-center gap-4`}>
      <Icon className={`w-6 h-6 ${palette.t}`} />
      <div>
        <div className="font-mono text-[10px] text-[#64748B] tracking-widest uppercase">{label}</div>
        <div className={`font-display font-black text-2xl ${palette.t}`}>{(value || 0).toLocaleString()}</div>
      </div>
    </div>
  );
}

function SignalRow({ signal }) {
  const [open, setOpen] = useState(false);
  const dt = new Date(signal.created_at);
  const rel = (() => {
    const sec = Math.max(1, Math.floor((Date.now() - dt.getTime()) / 1000));
    if (sec < 60) return `${sec}s ago`;
    if (sec < 3600) return `${Math.floor(sec/60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec/3600)}h ago`;
    return `${Math.floor(sec/86400)}d ago`;
  })();
  const kind = signal.type;
  const style = {
    verify:    { icon: UserCheck, color: "text-[#10B981]", border: "border-[#10B981]/30", bg: "bg-[#10B981]/5",  label: "VERIFIED" },
    bot_join:  { icon: Server,    color: "text-[#8B5CF6]", border: "border-[#8B5CF6]/30", bg: "bg-[#8B5CF6]/5",  label: "BOT ADDED" },
    bot_leave: { icon: UserX,     color: "text-[#EF4444]", border: "border-[#EF4444]/30", bg: "bg-[#EF4444]/5",  label: "BOT REMOVED" },
  }[kind] || { icon: Radio, color: "text-white", border: "border-white/10", bg: "bg-[#06070A]", label: kind?.toUpperCase() };
  const Icon = style.icon;
  const copy = (v) => { if (v) { navigator.clipboard?.writeText(String(v)); toast.success("Copied"); } };
  return (
    <div className={`border ${style.border} ${style.bg}`}>
      <div
        className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-white/5 transition-colors"
        onClick={() => setOpen(o => !o)}
        data-testid={`signal-row-${signal.id}`}
      >
        <Icon className={`w-4 h-4 ${style.color} flex-shrink-0`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`font-mono text-[10px] font-bold tracking-widest ${style.color}`}>{style.label}</span>
            {kind === "verify" && (
              <span className="text-sm text-white">
                <span className="font-display font-bold">{signal.global_name || signal.username || signal.user_id}</span>
                <span className="text-[#64748B]"> → </span>
                <span className="text-[#E2E8F0]">#{signal.guild_name || signal.guild_id}</span>
                {signal.prior_verifies_by_same_user > 1 && (
                  <span className="ml-2 px-1.5 py-0.5 border border-[#F59E0B]/50 text-[#F59E0B] text-[9px] font-mono rounded">
                    REPEAT ×{signal.prior_verifies_by_same_user}
                  </span>
                )}
                {signal.account_age_days !== null && signal.account_age_days < 7 && (
                  <span className="ml-2 px-1.5 py-0.5 border border-[#EF4444]/50 text-[#EF4444] text-[9px] font-mono rounded">
                    FRESH ACCT · {signal.account_age_days}d
                  </span>
                )}
              </span>
            )}
            {(kind === "bot_join" || kind === "bot_leave") && (
              <span className="text-sm text-white">
                <span className="font-display font-bold">{signal.guild_name || signal.guild_id}</span>
                {signal.member_count ? <span className="text-[#64748B]"> · {signal.member_count.toLocaleString()} members</span> : null}
                {signal.inviter_name ? <span className="text-[#64748B]"> · added by {signal.inviter_name}</span> : null}
                {signal.boost_level ? (
                  <span className="ml-2 px-1.5 py-0.5 border border-[#F47FFF]/50 text-[#F47FFF] text-[9px] font-mono rounded">BOOST T{signal.boost_level}</span>
                ) : null}
              </span>
            )}
          </div>
        </div>
        <span className="font-mono text-[10px] text-[#64748B] flex-shrink-0 whitespace-nowrap" title={dt.toLocaleString()}>{rel}</span>
      </div>

      {open && (
        <div className="border-t border-white/10 p-3 bg-[#06070A]/50">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-1.5 font-mono text-[11px]">
            {kind === "verify" && (
              <>
                <F label="User ID"             value={signal.user_id} onCopy={() => copy(signal.user_id)} />
                <F label="Username"            value={signal.username} />
                <F label="Global Name"         value={signal.global_name} />
                <F label="Discriminator"       value={signal.discriminator} />
                <F label="Public Flags"        value={signal.public_flags} />
                <F label="Locale"              value={signal.locale} />
                <F label="MFA Enabled"         value={signal.mfa_enabled ? "yes" : "no"} />
                <F label="Email Verified"      value={signal.verified_email === true ? "yes" : signal.verified_email === false ? "no" : "—"} />
                <F label="Account Created"     value={signal.account_created_at ? new Date(signal.account_created_at).toLocaleDateString() : null} />
                <F label="Account Age"         value={signal.account_age_days !== null ? `${signal.account_age_days} days` : null} />
                <F label="Guild ID"            value={signal.guild_id} onCopy={() => copy(signal.guild_id)} />
                <F label="Guild Owner ID"      value={signal.guild_owner_id} onCopy={() => copy(signal.guild_owner_id)} />
                <F label="Guild Members"       value={signal.guild_member_count?.toLocaleString()} />
                <F label="Guild Online"        value={signal.guild_presence_count?.toLocaleString()} />
                <F label="Joined Guild At"     value={signal.member_joined_at ? new Date(signal.member_joined_at).toLocaleString() : null} />
                <F label="Server Nickname"     value={signal.member_nick} />
                <F label="Current Roles"       value={signal.member_role_count} />
                <F label="Role Granted"        value={signal.role_granted_id} onCopy={() => copy(signal.role_granted_id)} />
                <F label="Role Removed"        value={signal.role_removed_id} onCopy={() => copy(signal.role_removed_id)} />
                <F label="Prior Verifies"      value={signal.prior_verifies_by_same_user} />
                <F label="Client IP"           value={signal.client_ip} />
                <F label="Accept-Language"     value={signal.accept_language} />
                <F label="User-Agent"          value={signal.user_agent} full />
              </>
            )}
            {(kind === "bot_join" || kind === "bot_leave") && (
              <>
                <F label="Guild ID"            value={signal.guild_id} onCopy={() => copy(signal.guild_id)} />
                <F label="Guild Name"          value={signal.guild_name} />
                <F label="Owner ID"            value={signal.owner_id} onCopy={() => copy(signal.owner_id)} />
                <F label="Description"         value={signal.guild_description} full />
                <F label="Members"             value={signal.member_count?.toLocaleString()} />
                <F label="Max Members"         value={signal.max_members?.toLocaleString()} />
                <F label="Channels"            value={signal.channel_count} />
                <F label="Roles"               value={signal.role_count} />
                <F label="Emojis"              value={signal.emoji_count} />
                <F label="Verification Lvl"    value={signal.verification_level} />
                <F label="Preferred Locale"    value={signal.preferred_locale} />
                <F label="Boost Tier"          value={signal.boost_level ? `Tier ${signal.boost_level}` : "—"} />
                <F label="Boost Count"         value={signal.boost_count} />
                <F label="Inviter ID"          value={signal.inviter_id} onCopy={() => copy(signal.inviter_id)} />
                <F label="Inviter Name"        value={signal.inviter_name} />
                <F label="Guild Features"      value={signal.features?.length ? signal.features.join(", ") : null} full />
              </>
            )}
            <F label="Signal ID"               value={signal.id} full />
            <F label="Timestamp"               value={dt.toISOString()} full />
          </div>
        </div>
      )}
    </div>
  );
}

function F({ label, value, onCopy, full }) {
  const empty = value === null || value === undefined || value === "" ;
  return (
    <div className={`flex items-start gap-2 ${full ? "sm:col-span-2 lg:col-span-3" : ""}`}>
      <span className="text-[9px] tracking-widest text-[#64748B] uppercase min-w-[110px] pt-0.5 flex-shrink-0">{label}</span>
      <span className={`flex-1 break-all ${empty ? "text-[#64748B]" : "text-[#E2E8F0]"}`}>{empty ? "—" : String(value)}</span>
      {!empty && onCopy && (
        <button onClick={(e) => { e.stopPropagation(); onCopy(); }} className="text-[#64748B] hover:text-white flex-shrink-0" title="Copy">
          <HashIcon className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

function GlobalBanSyncPanel() {
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/owner/global-ban-sync-status");
      setStatus(r.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const syncNow = async () => {
    if (!confirm("Sync all global bans to every ModNode server?")) return;
    setSyncing(true);
    try {
      const r = await api.post("/owner/sync-global-bans");
      toast.success(`Synced ${r.data.total_applied} bans across ${r.data.guilds} servers`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  return (
    <div className="border border-[#10B981]/30 bg-[#10B981]/5 p-5" data-testid="gban-sync-panel">
      <div className="flex items-center gap-2 mb-3">
        <RefreshCcw className="w-4 h-4 text-[#10B981]" />
        <h3 className="font-display font-bold uppercase">Auto-Sync To New Servers</h3>
        <span className="ml-auto px-2 py-1 border border-[#10B981]/40 text-[#10B981] font-mono text-[10px] tracking-widest bg-[#10B981]/5">
          BACKGROUND · 5MIN
        </span>
      </div>
      <p className="text-xs text-[#94A3B8] mb-4">
        Every 5 minutes ModNode detects newly-joined servers and auto-applies the entire global ban registry. You can also sync manually right now.
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="border border-white/10 bg-[#06070A] p-3">
          <div className="font-mono text-[9px] tracking-widest text-[#64748B] uppercase mb-1">Known Servers</div>
          <div className="font-display font-bold text-lg text-[#8B5CF6]">{status?.known_guilds ?? "—"}</div>
        </div>
        <div className="border border-white/10 bg-[#06070A] p-3">
          <div className="font-mono text-[9px] tracking-widest text-[#64748B] uppercase mb-1">Current Servers</div>
          <div className="font-display font-bold text-lg text-[#10B981]">{status?.current_guilds ?? "—"}</div>
        </div>
        <div className="col-span-2 border border-white/10 bg-[#06070A] p-3">
          <div className="font-mono text-[9px] tracking-widest text-[#64748B] uppercase mb-1">Pending Sync</div>
          <div className="font-mono text-xs text-[#F59E0B] truncate">
            {status?.pending_new_guilds?.length
              ? status.pending_new_guilds.map(g => g.name).join(", ")
              : "— none —"}
          </div>
        </div>
      </div>
      <button
        data-testid="gban-sync-now-btn"
        onClick={syncNow}
        disabled={syncing}
        className="px-6 py-2.5 bg-[#10B981] text-black font-display font-bold hover:bg-[#10B981]/80 disabled:opacity-50 flex items-center gap-2"
      >
        <RefreshCcw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
        {syncing ? "SYNCING..." : "SYNC NOW"}
      </button>
    </div>
  );
}

function V2Preview({ title, message, accent = "#10B981", footer = "ModNode" }) {
  const now = new Date();
  const rel = "just now";
  return (
    <div
      data-testid="v2-preview"
      className="bg-[#0D0E15] rounded-lg overflow-hidden border-l-4 shadow-lg"
      style={{ borderLeftColor: accent }}
    >
      {/* Container mimic — Discord Components V2 container is a bordered card */}
      <div className="px-4 py-3 space-y-2">
        <div className="text-white font-bold text-lg leading-tight break-words">
          {title || <span className="text-white/30 italic">Title preview…</span>}
        </div>
        <div className="h-px bg-white/10" />
        <div className="text-[#E2E8F0] text-sm whitespace-pre-wrap break-words leading-relaxed">
          {message || <span className="text-white/30 italic">Message body preview…</span>}
        </div>
        <div className="h-px bg-white/10" />
        <div className="text-[#949ba4] text-xs">
          {footer} · <span className="text-[#949ba4]/80">{rel}</span>
        </div>
      </div>
    </div>
  );
}

function OwnerStat({ label, value, accent = "cyan" }) {  const bar = accent === 'cyan' ? 'bg-[#8B5CF6]' : accent === 'volt' ? 'bg-[#10B981]' : accent === 'warn' ? 'bg-[#F59E0B]' : 'bg-[#EF4444]';
  const text = accent === 'cyan' ? 'text-[#8B5CF6]' : accent === 'volt' ? 'text-[#10B981]' : accent === 'warn' ? 'text-[#F59E0B]' : 'text-[#EF4444]';
  return (
    <div className="border border-white/10 bg-[#0D0E15] p-5 relative overflow-hidden">
      <div className={`absolute left-0 top-0 bottom-0 w-[3px] ${bar}`} />
      <div className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">{label}</div>
      <div className={`font-display font-black text-3xl mt-1 ${text}`}>{value ?? "—"}</div>
    </div>
  );
}


// ─── Founder sub-panels ──────────────────────────────────────

function FoundersFeed() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    try { const r = await api.get("/owner/actions-feed?limit=200"); setEvents(r.data.events || []); }
    catch { toast.error("Failed to load feed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);
  return (
    <div className="border border-white/10 bg-[#0D0E15]" data-testid="founder-feed">
      <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-[#10B981]" />
          <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Live Feed · {events.length}</span>
        </div>
        <span className="font-mono text-[10px] text-[#64748B]">auto-refresh 15s</span>
      </div>
      <div className="max-h-[70vh] overflow-y-auto">
        {loading && <div className="p-6 text-center font-mono text-xs text-[#10B981]">loading…</div>}
        {!loading && events.length === 0 && <div className="p-8 text-center font-mono text-xs text-[#64748B]">No events yet.</div>}
        {events.map((e, i) => (
          <div key={i} className="px-5 py-2.5 border-b border-white/5 hover:bg-[#141622] flex items-center gap-3">
            <span className={`w-1.5 h-1.5 rounded-full ${e.kind === "ai_flag" ? "bg-[#F59E0B]" : "bg-[#8B5CF6]"}`} />
            <span className="font-mono text-[10px] text-[#64748B] w-40 shrink-0">{new Date(e.ts).toLocaleString()}</span>
            <span className={`px-1.5 py-0.5 border rounded text-[10px] font-mono ${e.kind === "ai_flag" ? "border-[#F59E0B]/40 text-[#F59E0B]" : "border-[#8B5CF6]/40 text-[#8B5CF6]"}`}>
              {(e.type || "").toUpperCase()}
            </span>
            <span className="text-xs text-[#94A3B8] truncate max-w-[200px]">{e.guild_name}</span>
            <span className="text-sm text-white truncate flex-1">{e.target} — {e.reason}</span>
            {e.action_taken && <span className="font-mono text-[10px] text-[#10B981]">→ {e.action_taken}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function UserLookup() {
  const [uid, setUid] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const search = async () => {
    if (!uid.trim() || !/^\d{15,}$/.test(uid.trim())) return toast.error("Enter a valid Discord user ID (17-19 digits)");
    setLoading(true);
    try { const r = await api.get(`/owner/lookup/${uid.trim()}`); setData(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Lookup failed"); setData(null); }
    finally { setLoading(false); }
  };
  return (
    <div className="space-y-4" data-testid="user-lookup">
      <div className="border border-white/10 bg-[#0D0E15] p-5">
        <div className="flex items-center gap-2 mb-3">
          <Search className="w-4 h-4 text-[#8B5CF6]" />
          <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">User Lookup</span>
        </div>
        <div className="flex gap-2">
          <input value={uid} onChange={e => setUid(e.target.value)} onKeyDown={e => e.key === "Enter" && search()}
            placeholder="Paste a Discord user ID..."
            className="flex-1 bg-[#06070A] border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#8B5CF6] font-mono rounded-md" />
          <button onClick={search} disabled={loading} className="px-5 py-2 whitespace-nowrap bg-[#8B5CF6] hover:bg-[#7C3AED] text-white font-medium text-sm rounded-md disabled:opacity-50">
            {loading ? "..." : "Look up"}
          </button>
        </div>
      </div>
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Section title="Profile">
            {data.profile ? (
              <div className="flex items-center gap-3">
                {data.profile.avatar && <img src={`https://cdn.discordapp.com/avatars/${data.profile.id}/${data.profile.avatar}.png?size=64`} className="w-12 h-12 rounded-full" alt="" />}
                <div>
                  <div className="font-medium">{data.profile.global_name || data.profile.username}</div>
                  <div className="font-mono text-xs text-[#64748B]">{data.profile.username} · {data.profile.id}</div>
                </div>
              </div>
            ) : <div className="text-sm text-[#64748B]">User not found on Discord.</div>}
          </Section>
          <Section title={`Global ban: ${data.global_ban ? "YES" : "no"}`}>
            {data.global_ban ? <div className="text-sm text-[#EF4444]">{data.global_ban.reason}</div> : <div className="text-sm text-[#64748B]">Not globally banned.</div>}
          </Section>
          <Section title={`AI Flags · ${data.ai_flags.length}`}>
            <FlagListMini items={data.ai_flags} />
          </Section>
          <Section title={`Warnings · ${data.warnings.length}`}>
            <MiniList items={data.warnings.map(w => ({ line1: w.reason, line2: new Date(w.created_at).toLocaleString() }))} empty="No warnings." />
          </Section>
          <Section title={`Appeals · ${data.appeals.length}`}>
            <MiniList items={data.appeals.map(a => ({ line1: `${a.status.toUpperCase()} — ${a.appeal_reason.slice(0,90)}`, line2: new Date(a.created_at).toLocaleString() }))} empty="No appeals." />
          </Section>
          <Section title={`Shared servers · ${data.shared_guilds.length}`}>
            <MiniList items={data.shared_guilds.map(g => ({ line1: g.guild_name, line2: `Joined ${new Date(g.joined_at).toLocaleDateString()}` }))} empty="Not in any bot-shared server." />
          </Section>
        </div>
      )}
    </div>
  );
}

function AccessRoleManager() {
  const [holders, setHolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grantForm, setGrantForm] = useState({ user_id: "", guild_id: "" });
  const [servers, setServers] = useState([]);
  const load = async () => {
    try {
      const [h, s] = await Promise.all([api.get("/owner/access-role/holders"), api.get("/owner/servers")]);
      setHolders(h.data.holders || []); setServers(s.data.servers || []);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const grant = async () => {
    if (!grantForm.user_id || !grantForm.guild_id) return toast.error("Fill both fields");
    try { await api.post("/owner/access-role/grant", grantForm); toast.success("Access role granted"); setGrantForm({ user_id: "", guild_id: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Grant failed"); }
  };
  const revoke = async (h) => {
    if (!confirm(`Revoke access role from ${h.username}?`)) return;
    try { await api.post("/owner/access-role/revoke", { user_id: h.user_id, guild_id: h.guild_id }); toast.success("Revoked"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Revoke failed"); }
  };
  return (
    <div className="space-y-4" data-testid="access-role-manager">
      <div className="border border-white/10 bg-[#0D0E15] p-5">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound className="w-4 h-4 text-[#8B5CF6]" />
          <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Grant Access Role</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <input value={grantForm.user_id} onChange={e => setGrantForm(f => ({ ...f, user_id: e.target.value }))}
            placeholder="Discord user ID" className="bg-[#06070A] border border-white/10 px-3 py-2 text-sm rounded-md font-mono" />
          <select value={grantForm.guild_id} onChange={e => setGrantForm(f => ({ ...f, guild_id: e.target.value }))}
            className="bg-[#06070A] border border-white/10 px-3 py-2 text-sm rounded-md">
            <option value="">Select server…</option>
            {servers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button onClick={grant} className="px-5 py-2 bg-[#8B5CF6] hover:bg-[#7C3AED] text-white font-medium text-sm rounded-md">Grant</button>
        </div>
      </div>
      <div className="border border-white/10 bg-[#0D0E15]">
        <div className="px-5 py-3 border-b border-white/10 font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">
          Current Access Holders · {holders.length}
        </div>
        {loading && <div className="p-6 text-center font-mono text-xs text-[#8B5CF6]">loading…</div>}
        {!loading && holders.map(h => (
          <div key={h.user_id} className="flex items-center gap-3 px-5 py-2.5 border-b border-white/5">
            <img src={h.avatar ? `https://cdn.discordapp.com/avatars/${h.user_id}/${h.avatar}.png?size=64` : ""} className="w-8 h-8 rounded-full bg-[#141622]" alt="" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{h.username}</div>
              <div className="font-mono text-xs text-[#64748B]">{h.user_id} · via {h.guild_name}</div>
            </div>
            <button onClick={() => revoke(h)} className="px-3 py-1.5 border border-[#EF4444]/40 hover:border-[#EF4444] hover:text-[#EF4444] font-mono text-[10px] tracking-widest rounded-md">REVOKE</button>
          </div>
        ))}
        {!loading && holders.length === 0 && <div className="p-6 text-center font-mono text-xs text-[#64748B]">No one has the access role yet.</div>}
      </div>
    </div>
  );
}

function ServerManager() {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    try { const r = await api.get("/owner/servers"); setServers(r.data.servers || []); }
    catch { toast.error("Failed to load servers"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const leave = async (g) => {
    if (!confirm(`Bot will LEAVE ${g.name}. Sure?`)) return;
    try { await api.post(`/owner/servers/${g.id}/leave`); toast.success(`Left ${g.name}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const syncCmds = async (g) => {
    try { const r = await api.post(`/owner/servers/${g.id}/sync-commands`); toast.success(`Synced ${r.data.commands} commands`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
  };
  return (
    <div className="border border-white/10 bg-[#0D0E15]" data-testid="server-manager">
      <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2">
        <Server className="w-4 h-4 text-[#8B5CF6]" />
        <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Bot Server List · {servers.length}</span>
      </div>
      {loading && <div className="p-6 text-center font-mono text-xs text-[#8B5CF6]">loading…</div>}
      {!loading && servers.map(g => (
        <div key={g.id} className="flex items-center gap-3 px-5 py-3 border-b border-white/5">
          {g.icon_url ? <img src={g.icon_url} className="w-9 h-9 rounded-md" alt="" /> : <div className="w-9 h-9 bg-[#141622] rounded-md flex items-center justify-center text-xs">{g.name?.[0]}</div>}
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{g.name}</div>
            <div className="font-mono text-xs text-[#64748B]">{g.id}{g.member_count ? ` · ${g.member_count.toLocaleString()} members` : ""}</div>
          </div>
          <button onClick={() => syncCmds(g)} className="px-3 py-1.5 border border-[#8B5CF6]/40 hover:border-[#8B5CF6] hover:text-[#8B5CF6] font-mono text-[10px] tracking-widest rounded-md">SYNC CMDS</button>
          <button onClick={() => leave(g)} className="px-3 py-1.5 border border-[#EF4444]/40 hover:border-[#EF4444] hover:text-[#EF4444] font-mono text-[10px] tracking-widest rounded-md">LEAVE</button>
        </div>
      ))}
    </div>
  );
}

function KillSwitch() {
  const [state, setState] = useState({ ai_disabled: false, lockdown_all: false });
  const [saving, setSaving] = useState(false);
  const load = async () => { try { const r = await api.get("/owner/kill-switch"); setState(r.data); } catch { /* */ } };
  useEffect(() => { load(); }, []);
  const toggle = async (key) => {
    const next = !state[key];
    if (next && !confirm(`Enable ${key.replaceAll("_"," ")} across ALL servers?`)) return;
    setSaving(true);
    try { const r = await api.post("/owner/kill-switch", { [key]: next }); setState(r.data.state); toast.success(`${key} → ${next ? "ON" : "OFF"} · ${r.data.guilds_affected} servers affected`); }
    catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="border border-[#EF4444]/30 bg-[#0D0E15]" data-testid="kill-switch">
      <div className="px-5 py-3 border-b border-[#EF4444]/30 flex items-center gap-2 bg-gradient-to-r from-[#EF4444]/10 to-transparent">
        <PowerOff className="w-4 h-4 text-[#EF4444]" />
        <span className="font-mono text-[10px] tracking-widest text-[#EF4444] uppercase">Emergency Kill Switch</span>
      </div>
      <div className="p-5 space-y-3">
        <KillRow label="Disable AI Auto-Actions" hint="Instantly turns off auto-actions on every server. Flags keep landing in the feed, but nothing gets auto-deleted or auto-timed-out."
          value={state.ai_disabled} disabled={saving} onToggle={() => toggle("ai_disabled")} />
        <KillRow label="Lock Down Every Server" hint="Deny SEND_MESSAGES to @everyone across every text channel in every server the bot is in. Toggle off to restore."
          value={state.lockdown_all} disabled={saving} onToggle={() => toggle("lockdown_all")} danger />
      </div>
    </div>
  );
}

function KillRow({ label, hint, value, disabled, onToggle, danger }) {
  return (
    <div className={`p-4 border rounded-md ${value ? (danger ? "border-[#EF4444]/50 bg-[#EF4444]/10" : "border-[#F59E0B]/50 bg-[#F59E0B]/10") : "border-white/10 bg-[#06070A]"}`}>
      <div className="flex items-center justify-between gap-3 mb-1">
        <span className={`font-medium text-sm ${value && danger ? "text-[#EF4444]" : ""}`}>{label}</span>
        <button onClick={onToggle} disabled={disabled}
          className={`relative w-11 h-6 border rounded-full ${value ? (danger ? "bg-[#EF4444] border-[#EF4444]" : "bg-[#F59E0B] border-[#F59E0B]") : "bg-[#141622] border-white/15"} ${disabled ? "opacity-50" : "cursor-pointer"}`}>
          <span className={`absolute top-0.5 w-4 h-4 rounded-full transition-all ${value ? "left-6 bg-black" : "left-0.5 bg-white/80"}`} />
        </button>
      </div>
      <div className="text-xs text-[#94A3B8] leading-relaxed">{hint}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-white/10 bg-[#0D0E15] p-4">
      <div className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2">{title}</div>
      {children}
    </div>
  );
}

function BulkBanImport({ onImport }) {
  const [csv, setCsv] = useState("");
  const [reason, setReason] = useState("Bulk import");
  const [busy, setBusy] = useState(false);
  const importNow = async () => {
    // Accept commas, newlines, or spaces as separators; keep only digit runs
    const ids = Array.from(new Set((csv.match(/\d{15,20}/g) || [])));
    if (ids.length === 0) return toast.error("Paste at least one Discord user ID");
    if (!confirm(`Bulk-ban ${ids.length} user IDs across every ModNode server?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/owner/global-bans/bulk-import", { user_ids: ids, reason });
      toast.success(`Added ${r.data.added} · Skipped ${r.data.skipped} · ${r.data.attempted_applies} apply attempts across ${r.data.guilds} servers`);
      setCsv("");
      onImport?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCsv(String(ev.target.result || ""));
    reader.readAsText(f);
  };
  return (
    <div className="border border-white/10 bg-[#0D0E15] p-5" data-testid="bulk-ban-import">
      <div className="flex items-center gap-2 mb-3">
        <Upload className="w-4 h-4 text-[#F59E0B]" />
        <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Bulk Global Ban Import</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <textarea value={csv} onChange={e => setCsv(e.target.value)}
          placeholder="Paste user IDs (comma, newline, or space separated)..."
          rows={5}
          className="bg-[#06070A] border border-white/10 px-3 py-2 text-sm font-mono rounded-md outline-none focus:border-[#F59E0B]" />
        <div className="space-y-2">
          <input value={reason} onChange={e => setReason(e.target.value)}
            placeholder="Ban reason"
            className="w-full bg-[#06070A] border border-white/10 px-3 py-2 text-sm rounded-md" />
          <label className="block px-3 py-2 border border-dashed border-white/15 rounded-md text-sm text-[#94A3B8] cursor-pointer hover:border-[#F59E0B]">
            Or upload a CSV file
            <input type="file" accept=".csv,.txt" onChange={onFile} className="hidden" />
          </label>
          <button onClick={importNow} disabled={busy}
            className="w-full px-5 py-2 bg-[#F59E0B] hover:bg-[#e0a020] text-black font-medium text-sm rounded-md disabled:opacity-50">
            {busy ? "Importing..." : "Import & Ban Everywhere"}
          </button>
        </div>
      </div>
    </div>
  );
}
function MiniList({ items, empty }) {
  if (!items?.length) return <div className="text-sm text-[#64748B]">{empty}</div>;
  return <div className="space-y-1">{items.slice(0, 10).map((it, i) => (
    <div key={i} className="text-xs">
      <div className="text-white truncate">{it.line1}</div>
      <div className="text-[#64748B] font-mono">{it.line2}</div>
    </div>
  ))}</div>;
}
function FlagListMini({ items }) {
  if (!items?.length) return <div className="text-sm text-[#64748B]">Never flagged.</div>;
  return <div className="space-y-1">{items.slice(0, 8).map((f, i) => (
    <div key={i} className="text-xs">
      <span className={`px-1 py-px rounded ${f.classification?.severity === "critical" ? "text-[#EF4444]" : "text-[#F59E0B]"}`}>{f.classification?.severity}</span>
      <span className="text-white ml-2 truncate">{f.classification?.reason}</span>
    </div>
  ))}</div>;
}
